import { CATEGORY_PROCESS_TAX } from "./services-a";

const faq = (q, a) => ({ q, a });

const ADVISORY_PROCESS = [
  "Discovery discussion to understand your business and objective",
  "Scope, information checklist and engagement confirmation",
  "Analysis, preparation and review with your team",
  "Delivery, implementation support and follow-through",
];

export const ADVISORY_CATEGORIES = [
  {
    slug: "accounting-finance",
    title: "Accounting & Finance",
    intro:
      "Reliable accounting, reporting and finance-function support so business owners can focus on operations.",
    process: ADVISORY_PROCESS,
    services: [
      {
        slug: "accounting-bookkeeping",
        title: "Accounting & Bookkeeping",
        desc: "Systematic recording of day-to-day transactions with periodic review, reconciliations and clean year-end closing.",
        areas: ["Transaction recording", "Bank and ledger reconciliation", "Payables and receivables tracking", "Year-end closing support"],
        relevantFor: "Small and growing businesses that want dependable, up-to-date books.",
        documents: ["Bank statements", "Sales and purchase invoices", "Expense vouchers"],
        faqs: [
          faq("Can you work on our existing software?", "Yes, we commonly work with mainstream accounting software and can adapt to your setup."),
          faq("How frequently is bookkeeping done?", "Weekly, fortnightly or monthly cycles can be set up based on transaction volume."),
        ],
      },
      {
        slug: "mis-reporting",
        title: "MIS Reporting",
        desc: "Monthly management information reports covering profitability, cash flow, receivables and key performance indicators.",
        areas: ["Monthly P&L and variance views", "Cash-flow tracking", "Receivables ageing", "Custom KPI dashboards"],
        relevantFor: "Owners and management teams who want visibility into business performance.",
        documents: ["Books of account access", "Reporting requirements", "Budget data (if any)"],
        faqs: [
          faq("What does an MIS pack include?", "A standard pack covers financial performance and position; we tailor it to what you actually review."),
          faq("How soon after month-end?", "Reports are typically delivered within an agreed number of days after data cut-off."),
        ],
      },
      {
        slug: "financial-statements",
        title: "Financial Statements",
        desc: "Preparation of balance sheets, profit and loss accounts and notes in formats suitable for compliance, banks and stakeholders.",
        areas: ["Balance sheet and P&L preparation", "Schedule III formats (companies)", "Notes and disclosures", "Comparatives and analysis"],
        relevantFor: "Companies, firms and proprietorships needing formal financial statements.",
        documents: ["Trial balance", "Fixed asset register", "Loan and statutory details"],
        faqs: [
          faq("Do banks accept CA-prepared statements?", "Yes, financial statements prepared and certified by a Chartered Accountant are commonly required by lenders."),
          faq("Can statements be prepared mid-year?", "Yes, provisional statements can be prepared for specific purposes."),
        ],
      },
      {
        slug: "management-reporting",
        title: "Management Reporting",
        desc: "Structured reporting packs and review support for management meetings, boards and investors.",
        areas: ["Board and investor packs", "Segment and project reporting", "Budget vs actual analysis", "Review meeting support"],
        relevantFor: "Companies with boards, investors or multi-unit operations.",
        documents: ["Existing reports", "Chart of accounts", "Stakeholder requirements"],
        faqs: [
          faq("Can reporting be customised?", "Yes, formats and metrics are built around how your management actually reviews the business."),
          faq("Do you attend review meetings?", "On request, we participate in periodic review discussions."),
        ],
      },
      {
        slug: "virtual-cfo",
        title: "Virtual CFO",
        desc: "Ongoing senior finance oversight - planning, cash-flow management, compliance supervision and decision support - without a full-time hire.",
        areas: ["Financial planning and budgeting", "Cash-flow management", "Compliance supervision", "Management decision support"],
        relevantFor: "Growing businesses that need CFO-level guidance on a part-time basis.",
        documents: ["Financial history", "Business plans", "Current compliance status"],
        faqs: [
          faq("What does a Virtual CFO engagement include?", "A defined cadence of reviews, reports and advisory calls tailored to your stage and needs."),
          faq("Is it suitable for small businesses?", "Yes, the model is designed for businesses that need senior financial input without a full-time cost."),
        ],
      },
      {
        slug: "financial-planning-analysis",
        title: "Financial Planning & Analysis",
        desc: "Budgeting, forecasting, scenario analysis and unit-economics evaluation to support informed business decisions.",
        areas: ["Budgets and forecasts", "Scenario and sensitivity analysis", "Unit economics", "Variance analysis"],
        relevantFor: "Businesses planning growth, funding or cost restructuring.",
        documents: ["Historical financials", "Assumptions and plans", "Cost structure details"],
        faqs: [
          faq("How detailed are the forecasts?", "Granularity is set with you - from high-level projections to monthly line-item models."),
          faq("Can models be updated periodically?", "Yes, rolling updates can be part of the engagement."),
        ],
      },
    ],
  },
  {
    slug: "business-startup-advisory",
    title: "Business & Startup Advisory",
    intro:
      "Practical structuring, registration and compliance guidance for new ventures and growing businesses.",
    process: ADVISORY_PROCESS,
    services: [
      {
        slug: "startup-advisory",
        title: "Startup Advisory",
        desc: "End-to-end guidance for new ventures - entity choice, registrations, compliance calendar and financial readiness.",
        areas: ["Entity selection", "Registration roadmap", "Compliance calendar", "Finance function setup"],
        relevantFor: "Founders at idea, launch or early-growth stage.",
        documents: ["Business plan summary", "Founder KYC", "Proposed activity details"],
        faqs: [
          faq("Which entity is right for my startup?", "It depends on liability, taxation, compliance capacity and funding plans - we walk you through the trade-offs."),
          faq("When should we engage a CA?", "Ideally at the structuring stage, so registrations and records start correctly."),
        ],
      },
      {
        slug: "business-structuring",
        title: "Business Structuring",
        desc: "Evaluation and implementation of appropriate legal and tax structures for new and existing businesses.",
        areas: ["Structure comparison", "Conversion advisory", "Group and entity planning", "Tax-efficient organisation"],
        relevantFor: "Businesses starting up, expanding or reorganising.",
        documents: ["Current structure details", "Financials", "Growth plans"],
        faqs: [
          faq("Can a proprietorship convert to a company?", "Yes, conversion routes exist with specific procedures and tax considerations."),
          faq("Does restructuring have tax impact?", "It can; we evaluate implications before any step is taken."),
        ],
      },
      {
        slug: "business-registration",
        title: "Business Registration",
        desc: "Registration of proprietorships, partnerships, LLPs and private limited companies with coordinated post-incorporation steps.",
        areas: ["Entity incorporation", "PAN / TAN coordination", "Bank account documentation support", "Post-incorporation registrations"],
        relevantFor: "Entrepreneurs registering a new business.",
        documents: ["Founder KYC", "Proposed names", "Registered office proof"],
        faqs: [
          faq("How long does incorporation take?", "Typically one to three weeks depending on the entity type and document readiness."),
          faq("What happens after incorporation?", "Tax registrations, bank account opening and compliance setup follow - we coordinate these steps."),
        ],
      },
      {
        slug: "financial-modelling",
        title: "Financial Modelling",
        desc: "Structured financial models for fundraising, lending and internal planning, with clear assumptions and scenarios.",
        areas: ["Revenue and cost modelling", "Funding requirement analysis", "Scenario toggles", "Investor-ready outputs"],
        relevantFor: "Startups and businesses raising funds or planning expansion.",
        documents: ["Historical data", "Assumptions", "Pitch or plan documents"],
        faqs: [
          faq("Do investors expect a model?", "Most institutional investors expect a defensible financial model alongside the pitch."),
          faq("Can the model be updated later?", "Yes, models are built to be maintainable and updatable."),
        ],
      },
      {
        slug: "compliance-planning",
        title: "Compliance Planning",
        desc: "A mapped calendar of tax, corporate and regulatory compliances with ownership and timelines, so nothing is missed.",
        areas: ["Compliance mapping", "Calendar and reminders", "Responsibility assignment", "Periodic status review"],
        relevantFor: "Businesses managing multiple registrations and recurring filings.",
        documents: ["Registration details", "Current filing status", "Entity structure"],
        faqs: [
          faq("Which compliances apply to us?", "We map applicable compliances based on your entity type, registrations and activities."),
          faq("Do you track deadlines for us?", "Yes, ongoing compliance management can include tracking and reminders."),
        ],
      },
      {
        slug: "business-process-advisory",
        title: "Business Process Advisory",
        desc: "Review and improvement of finance and compliance processes - invoicing, documentation, approvals and record-keeping.",
        areas: ["Process review", "Documentation standards", "Internal control basics", "Software and tooling guidance"],
        relevantFor: "Businesses experiencing growth-related process strain.",
        documents: ["Current process descriptions", "Sample documents", "Pain points"],
        faqs: [
          faq("Is this only about accounting?", "No - it covers how financial information flows through your operations."),
          faq("Do you recommend software?", "We suggest categories and options suited to your scale; final selection stays with you."),
        ],
      },
    ],
  },
  {
    slug: "corporate-regulatory-compliance",
    title: "Corporate & Regulatory Compliance",
    intro:
      "Company law and regulatory compliance services for companies, LLPs and other registered entities.",
    process: ADVISORY_PROCESS,
    services: [
      {
        slug: "mca-roc-compliance",
        title: "MCA / ROC Compliance",
        desc: "Annual filings, event-based forms and statutory register maintenance under the Companies Act and LLP Act.",
        areas: ["Annual returns and financial filing", "Event-based forms (directors, charges etc.)", "Statutory registers", "Compliance status reviews"],
        relevantFor: "Companies and LLPs with MCA filing obligations.",
        documents: ["Incorporation documents", "Financial statements", "Board / partner details"],
        faqs: [
          faq("What are the key annual filings?", "Companies generally file AOC-4 and MGT-7; LLPs file Form 11 and Form 8, within prescribed timelines."),
          faq("What are the consequences of default?", "Additional fees accumulate and directors can face disqualification in prolonged default cases."),
        ],
      },
      {
        slug: "company-llp-compliance",
        title: "Company / LLP Compliance",
        desc: "Ongoing secretarial-compliance support covering meetings, resolutions, registers and routine corporate actions.",
        areas: ["Board and general meeting support", "Resolutions and minutes", "Registers and records", "Routine corporate actions"],
        relevantFor: "Private limited companies and LLPs seeking organised ongoing compliance.",
        documents: ["Corporate records", "Charter documents", "Stakeholder details"],
        faqs: [
          faq("Do small companies have relaxed norms?", "Certain relaxations apply to small companies and OPCs; applicability is evaluated case by case."),
          faq("Can compliance be outsourced fully?", "Yes, recurring compliance management can be handled on a retainer basis."),
        ],
      },
      {
        slug: "corporate-compliance",
        title: "Corporate Compliance",
        desc: "Broad corporate compliance reviews ensuring governance basics, filings and documentation remain in order.",
        areas: ["Compliance health checks", "Gap identification", "Regularisation support", "Documentation standards"],
        relevantFor: "Companies preparing for audits, funding or simply better hygiene.",
        documents: ["Corporate records", "Filing history", "Registers"],
        faqs: [
          faq("What is a compliance health check?", "A structured review of filings, registers and records to identify gaps and recommend corrections."),
          faq("Can past lapses be fixed?", "Most defaults can be regularised through late filings or specific schemes when available."),
        ],
      },
      {
        slug: "regulatory-compliance",
        title: "Regulatory Compliance",
        desc: "Identification and management of applicable regulatory compliances beyond tax and company law, based on your industry.",
        areas: ["Applicability mapping", "Registration and renewal tracking", "Filing support", "Update monitoring"],
        relevantFor: "Businesses operating in regulated or multi-licence environments.",
        documents: ["Licence inventory", "Activity description", "Existing compliance records"],
        faqs: [
          faq("Which regulators could apply to us?", "It depends on your industry - labour, local municipal, sector-specific regulators and others may apply."),
          faq("Do you monitor changes?", "We keep clients informed of changes relevant to their registered compliances."),
        ],
      },
    ],
  },
  {
    slug: "specialised-advisory",
    title: "Specialised Advisory",
    intro:
      "Focused advisory for real estate, cross-border and foreign exchange related matters requiring specialist attention.",
    process: ADVISORY_PROCESS,
    services: [
      {
        slug: "rera-advisory-compliance",
        title: "RERA Advisory & Compliance",
        desc: "MahaRERA registration support, ongoing compliance, and CA certifications required under RERA for developers and agents.",
        areas: ["Project registration support", "Form 3 CA certification", "Separate account compliance", "Quarterly and annual updates"],
        relevantFor: "Real estate developers and agents operating in Maharashtra.",
        documents: ["Project details", "Land and approval documents", "Financial records of the project"],
        faqs: [
          faq("What is Form 3 under RERA?", "A CA certification on withdrawals from the designated project account, required periodically under MahaRERA."),
          faq("Who must register under RERA?", "Promoters of registrable real estate projects and real estate agents, as defined under the Act."),
        ],
      },
      {
        slug: "fema-advisory",
        title: "FEMA Advisory",
        desc: "Advisory on foreign exchange regulations covering inbound and outbound investments, remittances and reporting.",
        areas: ["FDI and ODI advisory", "FEMA reporting (FC-GPR etc.)", "Compounding guidance", "Remittance compliance"],
        relevantFor: "Businesses and individuals with foreign investment or remittance activity.",
        documents: ["Transaction details", "Bank advices", "Entity documents"],
        faqs: [
          faq("What is FEMA?", "The Foreign Exchange Management Act governs cross-border transactions involving India."),
          faq("Are there reporting deadlines?", "Yes, most FEMA reports have strict timelines after the transaction event."),
        ],
      },
      {
        slug: "cross-border-tax-matters",
        title: "Cross-border Tax Matters",
        desc: "Tax advisory for transactions and structures spanning India and other jurisdictions, coordinated with DTAA positions.",
        areas: ["Withholding analysis", "Permanent establishment evaluation", "Treaty positions", "Documentation guidance"],
        relevantFor: "Companies and individuals with cross-border payments, services or structures.",
        documents: ["Contracts", "Payment details", "Counterparty tax residency"],
        faqs: [
          faq("When does withholding apply on foreign payments?", "It depends on the nature of payment, the payee's residency and treaty provisions."),
          faq("Do you coordinate with foreign advisors?", "Where matters span jurisdictions, we coordinate with your overseas advisors as needed."),
        ],
      },
      {
        slug: "foreign-remittance-advisory",
        title: "Foreign Remittance Advisory",
        desc: "Compliance support for remittances into and out of India, including Form 15CA / 15CB requirements.",
        areas: ["Form 15CA / 15CB certification", "Remittance documentation", "TDS on foreign payments", "Bank liaison support"],
        relevantFor: "Anyone sending or receiving significant cross-border payments.",
        documents: ["Invoice / purpose documents", "Payee details", "Tax residency information"],
        faqs: [
          faq("What is Form 15CB?", "A CA certificate on the taxability of a foreign remittance, required in specified cases before remittance."),
          faq("Is 15CA always required?", "Applicability depends on the nature and amount of the remittance and available exemptions."),
        ],
      },
      {
        slug: "export-import-compliance",
        title: "Export / Import Compliance",
        desc: "Tax and regulatory compliance support for exporters and importers, including IEC, LUT and refund-related aspects.",
        areas: ["IEC registration support", "LUT and zero-rating compliance", "Export incentive documentation", "Import payment compliance"],
        relevantFor: "Businesses engaged in cross-border trade of goods or services.",
        documents: ["IEC and registration details", "Shipping / export documents", "Bank realisation records"],
        faqs: [
          faq("Is GST charged on exports?", "Exports are zero-rated - either under LUT without tax payment, or on payment with refund claim."),
          faq("What is an IEC?", "The Importer Exporter Code issued by DGFT, required for most import-export activity."),
        ],
      },
      {
        slug: "lut-advisory",
        title: "LUT Advisory",
        desc: "Filing and annual renewal of Letter of Undertaking for zero-rated exports without payment of IGST.",
        areas: ["LUT eligibility check", "Filing on GST portal", "Annual renewal tracking", "Record-keeping guidance"],
        relevantFor: "Exporters of goods or services and supplies to SEZ.",
        documents: ["GST registration details", "Export details", "Authorised signatory information"],
        faqs: [
          faq("How long is a LUT valid?", "A LUT is valid for the financial year in which it is furnished and must be renewed annually."),
          faq("What if LUT is not filed?", "IGST must be paid on export invoices, with refund claimed subsequently - affecting working capital."),
        ],
      },
    ],
  },
  {
    slug: "registrations-licences",
    title: "Registrations & Licences",
    intro:
      "Business registrations and licence support to get new and existing ventures properly set up.",
    process: CATEGORY_PROCESS_TAX,
    services: [
      {
        slug: "udyam-registration",
        title: "Udyam Registration",
        desc: "MSME (Udyam) registration assistance to obtain recognition and access applicable benefits for micro, small and medium enterprises.",
        areas: ["Eligibility evaluation", "Online registration", "Certificate issuance support", "Update and re-classification"],
        relevantFor: "Micro, small and medium enterprises seeking MSME recognition.",
        documents: ["Aadhaar of proprietor / partners", "PAN", "Business activity details"],
        faqs: [
          faq("Is Udyam registration free?", "The government portal does not charge a fee; professional assistance, if engaged, is for preparation and accuracy."),
          faq("Who can register?", "Most manufacturing and service enterprises within MSME investment and turnover limits."),
        ],
      },
      {
        slug: "shop-establishment-registration",
        title: "Shop & Establishment Registration",
        desc: "Registration under the Maharashtra Shops and Establishments law for offices and commercial establishments.",
        areas: ["New registration", "Renewals and amendments", "Employee detail updates", "Compliance guidance"],
        relevantFor: "Any office, shop or commercial establishment operating in Maharashtra.",
        documents: ["Address proof", "Identity of employer", "Employee count details"],
        faqs: [
          faq("Is this registration mandatory?", "Yes, establishments in Maharashtra are generally required to register within the prescribed period of commencing business."),
          faq("How long does it take?", "Online applications are typically processed quickly once documents are in order."),
        ],
      },
      {
        slug: "fssai-registration",
        title: "FSSAI Registration",
        desc: "FSSAI food business registration and licence application support for food-related ventures.",
        areas: ["Category and licence-type evaluation", "Application filing", "Clarification handling", "Renewal reminders"],
        relevantFor: "Restaurants, food manufacturers, traders, cloud kitchens and food startups.",
        documents: ["Business details", "Food category information", "Premises documents"],
        faqs: [
          faq("Which FSSAI licence do I need?", "Basic, State or Central licence depends on turnover and activity; we evaluate the correct category."),
          faq("How long is the licence valid?", "Licences are issued for chosen periods up to five years and must be renewed."),
        ],
      },
      {
        slug: "rera-registration",
        title: "RERA-related Registrations & Compliance",
        desc: "MahaRERA agent registration and promoter-side registration and compliance support for real estate projects.",
        areas: ["Agent registration", "Project registration coordination", "Account and certification compliance", "Portal update support"],
        relevantFor: "Real estate agents and promoters in Maharashtra.",
        documents: ["Applicant KYC", "Project / business details", "Financial documents as applicable"],
        faqs: [
          faq("Do agents need separate registration?", "Yes, real estate agents facilitating sales in registrable projects must obtain MahaRERA agent registration."),
          faq("Can registration be done online?", "Yes, applications are filed through the MahaRERA portal."),
        ],
      },
      {
        slug: "other-business-registrations",
        title: "Other Business Registrations",
        desc: "Coordinated support for other applicable registrations such as professional tax, trade licences and sector-specific registrations.",
        areas: ["Applicability mapping", "Application preparation", "Follow-up and issuance", "Renewal tracking"],
        relevantFor: "Businesses needing a complete registration setup in one coordinated effort.",
        documents: ["Entity documents", "Activity description", "Premises proof"],
        faqs: [
          faq("How do I know which registrations apply?", "We map registrations based on your activity, location and workforce."),
          faq("Can all registrations be handled together?", "Yes, we coordinate multiple registrations as a single setup exercise."),
        ],
      },
    ],
  },
];
